import type { PolymorphicRef } from '@sds-system/types';
import { useForkRef } from '@sds-system/utils';
import type { ChangeEventHandler, ElementType } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Column } from '../Column';
import { Row } from '../Row';
import { Typography } from '../Typography';
import { TextareaInput, TextareaRoot, TextareaWrapper } from './Textarea.styled';
import type { TextareaComponent, TextareaProps } from './Textarea.types';

export const Textarea: TextareaComponent = forwardRef(
  <T extends ElementType = 'textarea'>(
    {
      errorMessages,
      maxLength = 200,
      onChange,
      name,
      successMessages,
      isShowLength,
      isError,
      isSuccess,
      disabled,
      styledComponents,
      lengthPosition = 'out',
      ...props
    }: TextareaProps<T>,
    ref: PolymorphicRef<T>,
  ) => {
    const textareaRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef<HTMLInputElement>(textareaRef, ref);
    const [length, setLength] = useState(0);

    const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      const currentLength = e.target.value.length;

      if (currentLength > maxLength) {
        e.target.value = e.target.value.substring(0, maxLength);
      }

      setLength(e.target.value.length);
      if (onChange) onChange(e);
    };

    useEffect(() => {
      if (textareaRef.current) {
        setLength(textareaRef.current?.value.length);
      }
    }, []);

    const isShowTextareaBottom =
      (isShowLength && lengthPosition === 'out') ||
      (errorMessages?.length || 0) > 0 ||
      (successMessages?.length || 0) > 0;

    return (
      <TextareaRoot {...styledComponents?.root}>
        <TextareaWrapper
          disabled={disabled}
          errorMessages={errorMessages}
          isError={isError}
          isSuccess={isSuccess}
          successMessages={successMessages}
          {...styledComponents?.wrapper}
        >
          <TextareaInput
            disabled={disabled}
            maxLength={maxLength}
            name={name}
            onChange={handleChange}
            {...props}
            ref={handleRef}
            {...styledComponents?.input}
          />
          {isShowLength && lengthPosition === 'in' && (
            <Typography color='text.tertiary' fontType='caption.regular' textAlign='end'>
              {length || 0}/{maxLength}
            </Typography>
          )}
        </TextareaWrapper>

        {isShowTextareaBottom && (
          <Row alignItems='start' gap={8} justifyContent='flex-end' width='100%'>
            {(errorMessages?.length || 0) > 0 && (
              <Column
                className={`textarea-errorMessages ${name}-textarea-errorMessages`}
                width='100%'
              >
                {errorMessages?.map((message) => (
                  <Typography color='text.error' fontType='caption.regular' key={message}>
                    {message}
                  </Typography>
                ))}
              </Column>
            )}
            {(successMessages?.length || 0) > 0 && (
              <Column
                className={`textarea-successMessages ${name}-textarea-successMessages`}
                mt={8}
              >
                {successMessages?.map((message) => (
                  <Typography color='text.success' fontType='caption.regular' key={message}>
                    {message}
                  </Typography>
                ))}
              </Column>
            )}
            {isShowLength && lengthPosition === 'out' && (
              <Typography color='text.tertiary' fontType='caption.regular' textAlign='end'>
                {length || 0}/{maxLength}
              </Typography>
            )}
          </Row>
        )}
      </TextareaRoot>
    );
  },
);
